/**
 * Journey: IVF, With Donor (eggs), Male
 *
 * Steps: Learn > Test > Clinic > Prep > Treatment > Wait
 *
 * For a man using donor eggs and his own sperm. A partner or surrogate
 * carries the pregnancy. The egg donor is a third party.
 */

import type { JourneyDefinition, PlanStep } from "../plan.types";

const learnStep: PlanStep = {
  id: "learn",
  title: "How IVF Works",
  subtitle: "The most effective fertility treatment",
  icon: "BookOpen",
  education: {
    context:
      "IVF (in vitro fertilization) creates embryos outside the body, selects the best one, and transfers it for pregnancy. You'll provide the sperm, and a donor provides the eggs. It's the most effective fertility treatment, with success rates of 40-50% per transfer when using donor eggs.",
    details:
      "The full process:\n1. Egg donor stimulation (10-14 days) — Your donor takes injectable medications to mature multiple eggs\n2. Egg retrieval (20 minutes) — Donor's eggs are collected under light sedation\n3. Your sperm sample (retrieval day) — You provide a fresh sample at the clinic\n4. Fertilization (day 0) — Eggs and your sperm are combined in the lab (or ICSI is used)\n5. Embryo culture (5-7 days) — Embryos develop to blastocyst stage\n6. Embryo transfer (5 minutes) — A single embryo is transferred to your partner or surrogate\n7. Two-week wait — ~14 days until a blood pregnancy test (beta hCG)\n\nYour role: select your egg donor, provide your sperm sample on retrieval day, and support your partner or surrogate through the transfer and wait.",
    learnMoreLink: "ivf-guide",
  },
  actions: [
    {
      label: "I understand this",
      variant: "primary",
      icon: "Check",
      signal: "complete-step",
    },
    {
      label: "Full IVF guide",
      variant: "outline",
      icon: "BookOpen",
      screen: "LearnScreen",
      params: { article: "ivf-guide" },
    },
  ],
  completionSignal: { type: "manual" },
  conditionModifiers: [
    {
      condition: "male-factor",
      additionalEducation:
        "Your semen analysis suggests male factor infertility. Your clinic will likely recommend ICSI (intracytoplasmic sperm injection), where a single healthy sperm is injected directly into each egg. ICSI bypasses natural fertilization barriers and is very effective — fertilization rates are 70-80%.",
    },
    {
      condition: "age-38-plus",
      additionalEducation:
        "After 40, sperm DNA fragmentation increases, which can affect embryo quality and miscarriage rates. Your clinic may recommend DNA fragmentation testing. Lifestyle optimization (avoiding heat, limiting alcohol, supplements) can help reduce fragmentation.",
    },
  ],
  ageSpecificContent: [
    {
      range: [0, 39],
      content:
        "Male age has a modest effect on IVF outcomes. Sperm quality is generally well-maintained through your 30s. Using donor eggs means egg quality is not a limiting factor.",
    },
    {
      range: [40, 44],
      content:
        "After 40, sperm DNA fragmentation increases slightly. Your clinic may recommend a DNA fragmentation test alongside the standard semen analysis.",
    },
    {
      range: [45, 99],
      content:
        "Over 45, sperm DNA fragmentation is higher. Your clinic may discuss DNA fragmentation testing and whether ICSI would benefit your cycle.",
    },
  ],
};

const testStep: PlanStep = {
  id: "test",
  title: "Your Fertility Testing",
  subtitle: "Sperm analysis and screening",
  icon: "TestTube",
  education: {
    context:
      "Your semen analysis determines the fertilization method (conventional vs. ICSI). Your egg donor is pre-screened by the agency or clinic — you don't need to coordinate their testing.",
  },
  actions: [
    {
      label: "Find a lab near you",
      variant: "primary",
      screen: "FindCareScreen",
    },
    {
      label: "Upload test results",
      variant: "outline",
      screen: "DocumentsScreen",
    },
  ],
  substeps: [
    {
      id: "semen-analysis",
      title: "Semen analysis",
      education:
        "Measures count, motility, and morphology. Results determine if conventional fertilization or ICSI is needed. Abstain 2-5 days before your appointment.",
      status: "not-started",
    },
    {
      id: "dna-fragmentation",
      title: "DNA fragmentation test (if recommended)",
      education:
        "Measures sperm DNA damage. High fragmentation (>30%) may warrant lifestyle changes or ICSI. Not always needed — your clinic will advise.",
      status: "not-started",
    },
    {
      id: "infectious-disease",
      title: "Infectious disease screening",
      education:
        "Required before any IVF cycle. Standard panel: HIV, Hepatitis B/C, syphilis, gonorrhea, chlamydia.",
      status: "not-started",
    },
  ],
  completionSignal: { type: "all-substeps" },
  optimizationWindow: {
    substepId: "semen-analysis",
    biologicalCycle: "~74 days for full sperm regeneration",
    baselineAction: "Get a baseline semen analysis",
    optimizationPeriod: "2-3 months",
    retestAction: "Retest to see improvement",
    lifestyle: [
      {
        title: "Avoid excessive heat",
        evidence: "strong",
        impact: "high",
        education:
          "Sperm production requires temperatures 2-4°C below body temperature. Hot tubs, saunas, tight underwear, and laptops on your lap all raise scrotal temperature.",
      },
      {
        title: "Limit alcohol",
        evidence: "strong",
        impact: "moderate",
        education:
          "Heavy alcohol use reduces testosterone and impairs sperm production. Limiting intake during your optimization window is prudent.",
      },
      {
        title: "Exercise regularly",
        evidence: "moderate",
        impact: "moderate",
        education:
          "Regular moderate exercise improves sperm parameters. Avoid extreme endurance exercise and heavy cycling, which can temporarily lower counts.",
      },
      {
        title: "Quit smoking/vaping",
        evidence: "strong",
        impact: "high",
        education:
          "Tobacco reduces sperm count by 20-30% and increases DNA fragmentation. Cannabis also impairs motility and morphology.",
      },
    ],
    supplements: [
      {
        title: "CoQ10 (200-400mg/day)",
        evidence: "moderate",
        impact: "moderate",
        education:
          "Supports mitochondrial function in sperm cells. Multiple studies show improvements in count and motility after 3 months.",
        action: {
          label: "Add to supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      },
      {
        title: "Zinc (30mg/day)",
        evidence: "moderate",
        impact: "moderate",
        education:
          "Essential for testosterone production and sperm development. Seminal fluid has 100x the zinc concentration of blood.",
        action: {
          label: "Add to supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      },
      {
        title: "Folate/L-methylfolate (400-800mcg/day)",
        evidence: "moderate",
        impact: "moderate",
        education:
          "Supports DNA synthesis in developing sperm. Combined with zinc, studies show improved count.",
        action: {
          label: "Add to supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      },
      {
        title: "Vitamin D (2000-4000 IU/day)",
        evidence: "moderate",
        impact: "low",
        education:
          "Vitamin D receptors exist on sperm cells. Deficiency is associated with lower motility.",
        action: {
          label: "Add to supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      },
    ],
    ageGuidance: [
      {
        bracket: "Under 40",
        range: [0, 39],
        recommendation: "optimize-then-start",
        explanation:
          "You have time to optimize. A 2-3 month window of supplements and lifestyle changes can meaningfully improve sperm quality before your IVF cycle.",
      },
      {
        bracket: "40-45",
        range: [40, 45],
        recommendation: "parallel",
        explanation:
          "Start optimizing now while coordinating with your donor and clinic. The timing often aligns well — donor coordination takes 1-2 months too.",
      },
      {
        bracket: "Over 45",
        range: [46, 99],
        recommendation: "start-now-optimize-alongside",
        explanation:
          "Don't delay the cycle for optimization. Start supplements and lifestyle changes now, but proceed with the cycle timeline.",
      },
    ],
  },
};

const clinicStep: PlanStep = {
  id: "clinic",
  title: "Find Your Clinic",
  subtitle: "Choose a clinic experienced with donor IVF",
  icon: "Building2",
  education: {
    context:
      "For donor egg IVF, look for clinics with strong donor egg success rates on SART.org. Key metrics: live birth rate per transfer with donor eggs. Also consider clinics affiliated with egg donor agencies, as this can streamline the process.",
  },
  actions: [
    {
      label: "Browse clinics near you",
      variant: "primary",
      screen: "FindCareScreen",
      params: { filter: "ivf" },
    },
    {
      label: "Compare clinic costs",
      variant: "outline",
      screen: "CostsOverviewScreen",
    },
  ],
  substeps: [
    {
      id: "research-clinics",
      title: "Research clinics",
      education:
        "Compare SART donor egg success rates. Ask about their egg donor coordination.",
      status: "not-started",
    },
    {
      id: "schedule-consultation",
      title: "Schedule a consultation",
      education: "30-60 minute initial consultation to review your case.",
      status: "not-started",
    },
    {
      id: "choose-clinic",
      title: "Choose your clinic",
      education: "Your clinic will start the onboarding process.",
      status: "not-started",
    },
  ],
  completionSignal: { type: "manual" },
};

const prepStep: PlanStep = {
  id: "prep",
  title: "Get Ready for Your Cycle",
  subtitle: "Donor selection, paperwork, and decisions",
  icon: "ClipboardCheck",
  education: {
    context:
      "IVF prep with donor eggs involves selecting your egg donor, medical history, insurance, and important decisions about genetic testing and transfer strategy.",
  },
  actions: [
    {
      label: "Continue medical history",
      variant: "primary",
      screen: "MedicalHistory",
    },
    {
      label: "Upload insurance card",
      variant: "secondary",
      screen: "CoverageFlow",
    },
  ],
  substeps: [
    {
      id: "select-egg-donor",
      title: "Select your egg donor",
      education:
        "Choose a donor through an agency or your clinic's donor program. You can review profiles with physical characteristics, education, health history, and genetic screening results. Your clinic can help guide this decision.",
      status: "not-started",
    },
    {
      id: "medical-history",
      title: "Complete your medical history",
      education:
        "Your health history is needed for consent forms and to identify any factors that affect sperm quality.",
      status: "not-started",
    },
    {
      id: "insurance-card",
      title: "Upload insurance card",
      education: "Needed for billing.",
      status: "not-started",
    },
    {
      id: "pgta-decision",
      title: "PGT-A decision",
      education:
        "Decide whether to genetically test embryos before transfer. Cost: $3,000-$5,000. With donor eggs, embryo quality is typically high, but PGT-A can still help select the best embryo.",
      status: "not-started",
    },
    {
      id: "transfer-plan",
      title: "Transfer plan discussion",
      education:
        "Fresh transfer (same cycle as donor retrieval) vs frozen transfer (separate cycle). Your clinic will recommend based on your specific situation.",
      status: "not-started",
    },
    {
      id: "consent-forms",
      title: "Sign consent forms",
      education:
        "Covers procedure, risks, embryo disposition, and egg donor agreements.",
      status: "not-started",
    },
    {
      id: "supplements",
      title: "Start supplements for sperm quality (optional)",
      education:
        "CoQ10, zinc, folate, and vitamin D support sperm quality. Start 2-3 months before if possible — sperm takes ~74 days to regenerate.",
      status: "not-started",
      actions: [
        {
          label: "See supplements",
          variant: "outline",
          screen: "SupplementsScreen",
        },
      ],
    },
  ],
  completionSignal: {
    type: "completion-percent",
    field: "prepReadiness",
    threshold: 80,
  },
};

const treatmentStep: PlanStep = {
  id: "treatment",
  title: "Your IVF Cycle",
  subtitle: "Active treatment with daily guidance",
  icon: "Syringe",
  education: {
    context:
      "Your cycle is active. Track appointments and key moments in the Care section.",
    details:
      "Your donor's cycle: stimulation (10-14 days) → trigger → egg retrieval.\n\nThen: fertilization with your sperm → embryo development (5-7 days) → transfer to your partner or surrogate → two-week wait.\n\nYour key moment: providing your sperm sample on retrieval day.\n\nYour Care page has the shared appointment calendar and your sample instructions.",
  },
  actions: [
    {
      label: "Go to Care",
      variant: "primary",
      icon: "Heart",
      screen: "CareOverview",
    },
    { label: "Set your start date", variant: "outline", icon: "Calendar" },
  ],
  completionSignal: { type: "external" },
};

const waitStep: PlanStep = {
  id: "wait",
  title: "The Two-Week Wait",
  subtitle: "Between transfer and pregnancy test",
  icon: "Clock",
  education: {
    context:
      "The two-week wait (TWW) is the period between embryo transfer and the pregnancy blood test.",
    details:
      "Here's what's happening:\nDays 1-3: The embryo hatches and begins attaching to the uterine lining\nDays 4-5: Implantation begins\nDays 6-8: hCG hormone starts being produced if implantation is successful\nDays 9-14: hCG levels rise enough to be detectable by blood test\n\nHow you can help:\n• Be patient — the person carrying may be anxious, emotional, or withdrawn. All of that is normal\n• Don't read too much into symptoms — cramping, spotting, and breast tenderness can mean pregnant OR not pregnant\n• Help with practical things — meals, errands, keeping life low-stress\n• Avoid pushing for home pregnancy tests before day 10 — they're unreliable this early\n• The blood test (beta hCG) is the only reliable answer\n\nThis is a tough wait for everyone involved. Your support matters more than you might think.",
  },
  actions: [
    {
      label: "Log how you're feeling today",
      variant: "primary",
      icon: "Heart",
    },
    { label: "Support resources", variant: "secondary", icon: "Users" },
    { label: "When is the blood test?", variant: "outline", icon: "Calendar" },
  ],
  completionSignal: { type: "external" },
};

export const IVF_DONOR_MALE: JourneyDefinition = {
  key: "ivf-with-donor-male",
  gender: "male",
  goal: "pregnancy",
  pathway: "with-donor",
  title: "IVF",
  description: "In vitro fertilization with donor eggs",
  steps: [learnStep, testStep, clinicStep, prepStep, treatmentStep, waitStep],
};
